import { useState, useCallback, useEffect } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import type {
  RefetchOptions,
  RefetchQueryFilters,
  QueryObserverResult
} from 'react-query';

import useGetUnreadFromHttp from 'hooks/messageCommunication/useGetUnreadFromHttp';
import useSendReadMessage from 'hooks/messageCommunication/useSendReadMessage';
import type {
  GetUnreadParams,
  UnreadNotification
} from 'api/messageCommunication/types';
import type { PagedList } from 'interfaces/general';

type Props = {
  realTimeNotifications: UnreadNotification[];
  realTimeTotalCount: number;
  receiverUserId: number;
  refetchRealTimeNotifications: <TPageData>(
    options?: RefetchOptions & RefetchQueryFilters<TPageData>
  ) => Promise<QueryObserverResult<PagedList<UnreadNotification>, unknown>>;
  setRealTimeNotifications: React.Dispatch<
    React.SetStateAction<UnreadNotification[]>
  >;
  setRealTimeNotificationsParams: (
    newParams?: Partial<GetUnreadParams>
  ) => void;
  setRealTimeTotalCount: React.Dispatch<React.SetStateAction<number>>;
};

type NotificationsFromHttpState = {
  handleLoadMoreNotificationsFromHttp: () => void;
  handleReadAllNotificationsFromHttp: () => void;
  handleReadNotificationFromHttp: (id: number) => void;
  isNotificationsFromHttpFetching: boolean;
  isNotificationsFromHttpLoading: boolean;
  notificationsFromHttp: UnreadNotification[];
  notificationsFromHttpTotalCount: number;
  searchForm: UseFormReturn<{ search: string }>;
  searchValue: string;
  setDebouncedSearchTerm: (term: string) => void;
};

/**
 * A custom hook that manages notifications fetched from an HTTP source.
 *
 * This hook provides functionality to handle real-time notifications,
 * search through notifications, and mark notifications as read. It
 * integrates with a backend service to fetch unread notifications
 * and updates the state accordingly.
 *
 * @param {Object} props - The properties for the hook.
 * @param {UnreadNotification[]} props.realTimeNotifications - The current real-time notifications.
 * @param {number} props.realTimeTotalCount - The total count of real-time notifications.
 * @param {number} props.receiverUserId - The ID of the user receiving notifications.
 * @param {Function} props.refetchRealTimeNotifications - Function to refetch real-time notifications.
 * @param {Function} props.setRealTimeNotifications - Function to set real-time notifications.
 * @param {Function} props.setRealTimeNotificationsParams - Function to set parameters for real-time notifications.
 * @param {Function} props.setRealTimeTotalCount - Function to set the total count of real-time notifications.
 *
/**
 * @type {Object} NotificationsFromHttpState
 * @property {Function} handleLoadMoreNotificationsFromHttp - Function to load more notifications.
 * @property {Function} handleReadAllNotificationsFromHttp - Function to mark all notifications as read.
 * @property {Function} handleReadNotificationFromHttp - Function to mark a specific notification as read.
 * @property {boolean} isNotificationsFromHttpFetching - Indicates if notifications are currently being fetched.
 * @property {boolean} isNotificationsFromHttpLoading - Indicates if notifications are currently loading.
 * @property {UnreadNotification[]} notificationsFromHttp - The notifications fetched from HTTP.
 * @property {number} notificationsFromHttpTotalCount - The total count of notifications fetched from HTTP.
 * @property {UseFormReturn<{ search: string }>} searchForm - The form used for searching notifications.
 * @property {string} searchValue - The current search value.
 * @property {Function} setDebouncedSearchTerm - Function to set the debounced search term.
 * 
 * @returns {NotificationsFromHttpState}
 */

function useNotificationsFromHttp({
  realTimeNotifications,
  realTimeTotalCount,
  receiverUserId,
  refetchRealTimeNotifications,
  setRealTimeNotifications,
  setRealTimeNotificationsParams,
  setRealTimeTotalCount
}: Props): NotificationsFromHttpState {
  const searchForm = useForm<{ search: string }>({
    defaultValues: {
      search: ''
    }
  });
  const searchValue = searchForm.watch('search');

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchValue);

  const { sendReadMessage } = useSendReadMessage();

  const [notificationsFromHttp, setNotificationsFromHttp] = useState<
    UnreadNotification[]
  >([]);
  const [notificationsFromHttpTotalCount, setNotificationsFromHttpTotalCount] =
    useState(0);
  const [isNotificationsFromHttpEnabled, setNotificationsFromHttpEnabled] =
    useState(false);

  const {
    isUnreadNotificationsFetching: isNotificationsFromHttpFetching,
    isUnreadNotificationsLoading: isNotificationsFromHttpLoading,
    params: paramsFromHttp,
    refetchUnreadNotifications: refetchNotificationsFromHttp,
    setUnreadNotificationsParams: setNotificationsFromHttpParams
  } = useGetUnreadFromHttp({
    enabled: isNotificationsFromHttpEnabled,
    receiverUserId,
    setEnabled: setNotificationsFromHttpEnabled,
    setNotifications: setNotificationsFromHttp,
    setTotalCount: setNotificationsFromHttpTotalCount
  });

  useEffect(() => {
    if (debouncedSearchTerm) {
      setNotificationsFromHttpParams({ text: debouncedSearchTerm });
      setNotificationsFromHttpEnabled(true);
    } else {
      setNotificationsFromHttpParams({ pageNumber: 1 });
    }
  }, [debouncedSearchTerm, setNotificationsFromHttpParams]);

  const handleReadNotificationFromHttp = useCallback(
    (notificationId: number) => {
      setNotificationsFromHttpTotalCount((prevCount) => prevCount - 1);
      setRealTimeTotalCount((prevCount) => prevCount - 1);

      setNotificationsFromHttp((prevNotifications) =>
        prevNotifications.filter(
          (notification) => notification.notificationId !== notificationId
        )
      );
      setRealTimeNotifications((prevNotifications) =>
        prevNotifications.filter(
          (notification) => notification.notificationId !== notificationId
        )
      );

      setNotificationsFromHttpParams({ pageNumber: 1 });

      sendReadMessage(
        { receiverUserId, notificationIds: [notificationId] },
        {
          onSuccess: async () => {
            if (
              notificationsFromHttpTotalCount !== 0 &&
              notificationsFromHttp.length === 1
            ) {
              await refetchNotificationsFromHttp();
            }

            if (
              realTimeTotalCount !== 0 &&
              realTimeNotifications.length === 1
            ) {
              await refetchRealTimeNotifications();
            }
          }
        }
      );
    },
    [
      setRealTimeTotalCount,
      setRealTimeNotifications,
      setNotificationsFromHttpParams,
      sendReadMessage,
      receiverUserId,
      notificationsFromHttpTotalCount,
      notificationsFromHttp.length,
      realTimeTotalCount,
      realTimeNotifications.length,
      refetchNotificationsFromHttp,
      refetchRealTimeNotifications
    ]
  );

  const handleReadAllNotificationsFromHttp = useCallback(() => {
    const notificationsWithNeedsAttention = notificationsFromHttp.filter(
      (notification) => notification.needsAttention
    );

    const filteredByNeedsAttention = realTimeNotifications.filter(
      (notification) => !notification.needsAttention
    );

    const notificationIds = filteredByNeedsAttention.map(
      (notification) => notification.notificationId
    );

    if (!notificationIds.length) return;

    setNotificationsFromHttp(notificationsWithNeedsAttention);

    setNotificationsFromHttpTotalCount(
      (prevCount) =>
        prevCount -
        notificationsFromHttp.length +
        notificationsWithNeedsAttention.length
    );

    setNotificationsFromHttpParams({ pageNumber: 1 });

    const filteredRealTimeNotifications = realTimeNotifications.filter(
      (realTimeNotification) =>
        !notificationsFromHttp.some(
          (notificationFromHttp) =>
            notificationFromHttp.notificationId ===
            realTimeNotification.notificationId
        )
    );

    setRealTimeNotifications(filteredRealTimeNotifications);
    setRealTimeTotalCount(
      (prevCount) => prevCount - filteredRealTimeNotifications.length
    );

    if (
      realTimeTotalCount !== 0 &&
      filteredRealTimeNotifications.length === 0
    ) {
      setRealTimeNotificationsParams({ pageNumber: 1 });
    }

    sendReadMessage(
      {
        receiverUserId,
        notificationIds
      },
      {
        onSuccess: async () => {
          const actualTotalCount =
            notificationsFromHttpTotalCount - notificationsFromHttp.length;

          if (actualTotalCount !== 0) {
            await refetchNotificationsFromHttp();
          } else {
            searchForm.setValue('search', '');
          }

          if (
            realTimeTotalCount !== 0 &&
            filteredRealTimeNotifications.length === 0
          ) {
            await refetchRealTimeNotifications();
          }
        }
      }
    );
  }, [
    notificationsFromHttp,
    notificationsFromHttpTotalCount,
    realTimeNotifications,
    realTimeTotalCount,
    receiverUserId,
    refetchNotificationsFromHttp,
    refetchRealTimeNotifications,
    searchForm,
    sendReadMessage,
    setNotificationsFromHttpParams,
    setRealTimeNotifications,
    setRealTimeNotificationsParams,
    setRealTimeTotalCount
  ]);

  const handleLoadMoreNotificationsFromHttp = useCallback(() => {
    setNotificationsFromHttpParams({
      pageNumber: paramsFromHttp.pageNumber + 1
    });

    setNotificationsFromHttpEnabled(true);
  }, [paramsFromHttp.pageNumber, setNotificationsFromHttpParams]);

  return {
    handleLoadMoreNotificationsFromHttp,
    handleReadAllNotificationsFromHttp,
    handleReadNotificationFromHttp,
    isNotificationsFromHttpFetching,
    isNotificationsFromHttpLoading,
    notificationsFromHttp,
    notificationsFromHttpTotalCount,
    searchForm,
    searchValue: debouncedSearchTerm,
    setDebouncedSearchTerm
  };
}

export default useNotificationsFromHttp;
